import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useStatements, usePostLedger } from '../hooks/useOps';
import { formatIDR } from '../lib/format';
import type { StatementLine } from '../api/types';

function thisYear(): string {
  return String(new Date().getFullYear());
}

function ProvisionalChip({ provisional }: { provisional: boolean }) {
  if (!provisional) {
    return <Chip size="small" color="success" variant="outlined" label="Final (period closed)" />;
  }
  return (
    <Tooltip title="This period is still open — figures may change. Close the fiscal period to finalize.">
      <Chip size="small" color="warning" variant="outlined" icon={<InfoOutlinedIcon />} label="Provisional" />
    </Tooltip>
  );
}

function LineTable({ rows, total, totalLabel }: { rows: StatementLine[]; total: string; totalLabel: string }) {
  return (
    <Table size="small">
      <TableBody>
        {rows.length === 0 && (
          <TableRow>
            <TableCell colSpan={3}>
              <Typography variant="body2" color="text.secondary">
                No entries.
              </Typography>
            </TableCell>
          </TableRow>
        )}
        {rows.map((r) => (
          <TableRow key={r.code}>
            <TableCell sx={{ color: 'text.secondary', width: 64 }}>{r.code}</TableCell>
            <TableCell>{r.name}</TableCell>
            <TableCell align="right">{formatIDR(r.amount)}</TableCell>
          </TableRow>
        ))}
        <TableRow>
          <TableCell />
          <TableCell sx={{ fontWeight: 700 }}>{totalLabel}</TableCell>
          <TableCell align="right" sx={{ fontWeight: 700 }}>
            {formatIDR(total)}
          </TableCell>
        </TableRow>
      </TableBody>
    </Table>
  );
}

export function StatementsPage() {
  const [period, setPeriod] = useState(thisYear());
  const { data, isLoading, error } = useStatements(period);
  const post = usePostLedger();

  return (
    <Box>
      <Stack direction="row" alignItems="center" gap={1} mb={2} flexWrap="wrap">
        <Typography variant="h5" component="h1" fontWeight={700}>
          Laporan Keuangan
        </Typography>
        {data && <ProvisionalChip provisional={data.pnl.provisional} />}
        <Box flexGrow={1} />
        <TextField
          size="small"
          label="Period (YYYY or YYYY-MM)"
          value={period}
          onChange={(e) => setPeriod(e.target.value.trim())}
          sx={{ width: 210 }}
        />
        <Button
          variant="outlined"
          onClick={() => post.mutate()}
          disabled={post.isPending}
        >
          {post.isPending ? 'Posting…' : 'Post ledger'}
        </Button>
      </Stack>

      <Typography variant="body2" color="text.secondary" mb={2}>
        Derived from the general ledger (the outbox drains automatically nightly; "Post ledger" forces it now).
        {post.data ? ` Posted ${post.data.posted} entr${post.data.posted === 1 ? 'y' : 'ies'}.` : ''}
      </Typography>

      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
          <CircularProgress />
        </Box>
      )}
      {error && <Alert severity="error">Failed to load statements: {(error as Error).message}</Alert>}

      {data && (
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="h6" fontWeight={600} mb={1}>
                Laba Rugi
              </Typography>
              <Typography variant="overline" color="text.secondary">
                Pendapatan
              </Typography>
              <LineTable rows={data.pnl.income} total={data.pnl.totalIncome} totalLabel="Total Pendapatan" />
              <Typography variant="overline" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                Beban
              </Typography>
              <LineTable rows={data.pnl.expenses} total={data.pnl.totalExpenses} totalLabel="Total Beban" />
              <Stack direction="row" justifyContent="space-between" mt={2} px={1}>
                <Typography fontWeight={700}>Laba (Rugi) Bersih</Typography>
                <Typography
                  fontWeight={700}
                  color={Number(data.pnl.netProfit) < 0 ? 'error.main' : 'success.main'}
                >
                  {formatIDR(data.pnl.netProfit)}
                </Typography>
              </Stack>
            </Paper>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="h6" fontWeight={600} mb={1}>
                Neraca <Typography component="span" variant="body2" color="text.secondary">(per {data.balanceSheet.asOf})</Typography>
              </Typography>
              <Typography variant="overline" color="text.secondary">
                Aset
              </Typography>
              <LineTable rows={data.balanceSheet.assets} total={data.balanceSheet.totalAssets} totalLabel="Total Aset" />
              <Typography variant="overline" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                Liabilitas & Ekuitas
              </Typography>
              <LineTable
                rows={[
                  ...data.balanceSheet.liabilities,
                  ...data.balanceSheet.equity,
                  { code: '—', name: 'Laba Tahun Berjalan', amount: data.balanceSheet.currentEarnings },
                ]}
                total={(Number(data.balanceSheet.totalLiabilities) + Number(data.balanceSheet.totalEquity)).toFixed(2)}
                totalLabel="Total Liabilitas + Ekuitas"
              />
              {data.balanceSheet.imbalance !== '0.00' && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  Neraca tidak balance (selisih {formatIDR(data.balanceSheet.imbalance)}). Periksa jurnal / posting.
                </Alert>
              )}
            </Paper>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="h6" fontWeight={600} mb={1}>
                Arus Kas
              </Typography>
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell>Saldo awal</TableCell>
                    <TableCell align="right">{formatIDR(data.cashFlow.opening)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Kas masuk</TableCell>
                    <TableCell align="right" sx={{ color: 'success.main' }}>
                      {formatIDR(data.cashFlow.inflows)}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Kas keluar</TableCell>
                    <TableCell align="right" sx={{ color: 'error.main' }}>
                      ({formatIDR(data.cashFlow.outflows)})
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Saldo akhir</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>
                      {formatIDR(data.cashFlow.closing)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Paper>
          </Grid>
        </Grid>
      )}
    </Box>
  );
}
